import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const dynamicMessages = [
  'Cut through the AI hype. Discover what works.',
  'Real examples. Tangible outcomes. Neutral insights.',
  'Explore AI solutions tailored to your business goals.',
];

const cards = [
  {
    title: 'Discover AI Use Cases',
    message: 'Browse curated AI examples tailored to your needs.',
    link: '/inquiries',
    icon: '🧩',
  },
  {
    title: 'Submit Your Challenge',
    message: 'Share your pain points and get actionable solutions.',
    link: '/submit-inquiry',
    icon: '💡',
  },
  {
    title: 'Get Expert Guidance',
    message: 'Chat with our AI experts to get clarity and insights.',
    link: '/chat',
    icon: '🤖',
  },
  {
    title: 'Success Stories',
    message: 'Learn how businesses succeeded using AI.',
    link: '/success-stories',
    icon: '🏆',
  },
  {
    title: 'Partner Spotlights',
    message: 'Explore our trusted partner ecosystem for AI delivery.',
    link: '/partners',
    icon: '🤝',
  },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const [messageIndex, setMessageIndex] = useState(0);

  useEffect(() => {
    const messageInterval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % dynamicMessages.length);
    }, 2500);

    return () => {
      clearInterval(messageInterval);
    };
  }, []);

  return (
    <div className="bg-gray-50 text-gray-800">
      {/* Storytelling Hero Section */}
      <section className="h-[50vh] flex items-center justify-center bg-white px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="text-center max-w-4xl"
        >
          <h1 className="text-5xl font-bold mb-6 text-gray-900 leading-tight">
            Cut Through the AI Hype. Discover What Works.
          </h1>
          <motion.p
            key={messageIndex}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
            className="text-xl text-gray-600 mb-8"
          >
            {dynamicMessages[messageIndex]}
          </motion.p>
          <div className="flex justify-center gap-4">
            <button
              onClick={() => navigate('/inquiries')}
              aria-label="Explore AI Use Cases"
              className="bg-black text-white px-6 py-3 rounded hover:bg-gray-800 transition"
            >
              Explore AI Use Cases
            </button>
            <button
              onClick={() => navigate('/submit-inquiry')}
              aria-label="Submit Your Challenge"
              className="border border-black text-black px-6 py-3 rounded hover:bg-gray-100 transition"
            >
              Submit Your Challenge
            </button>
          </div>
        </motion.div>
      </section>

      {/* Cards Section */}
      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
          {cards.map((card, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.2 }}
              viewport={{ once: true }}
              className="p-6 bg-gray-100 rounded-lg shadow-lg hover:shadow-2xl transition cursor-pointer"
              onClick={() => navigate(card.link)}
            >
              <div className="text-5xl mb-4 text-gray-700">{card.icon}</div>
              <h3 className="text-2xl font-semibold mb-2 text-gray-800">
                {card.title}
              </h3>
              <p className="text-gray-600">{card.message}</p>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default LandingPage;